import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Compass,
  Sun,
  Heart,
  Zap,
  Wind,
  Brain,
  Clock,
  Sparkles,
  Shield,
  Gift,
  ArrowRight,
  MessageSquare,
  Loader2,
  CheckCircle
} from "lucide-react";
import axios from "axios";

// Free tools data
const FREE_TOOLS = [
  {
    icon: Sun,
    name: "Daily Signal",
    description: "A daily reflection prompt to set your intention"
  },
  {
    icon: Heart,
    name: "Mood Tracker",
    description: "Track how you feel and spot patterns over time"
  },
  {
    icon: Zap,
    name: "Energy Check-In",
    description: "Understand what drains and fuels you"
  },
  {
    icon: Wind,
    name: "Breathing Exercise",
    description: "Reset your nervous system in 3 minutes"
  },
  {
    icon: Brain,
    name: "Thought Reframe",
    description: "Challenge the thoughts that hold you back"
  },
  {
    icon: Clock,
    name: "Focus Timer",
    description: "25-minute Pomodoro sessions for clear thinking"
  }
];

// Social proof statements
const SOCIAL_PROOF = [
  {
    icon: Compass,
    text: "No overwhelm. Just one daily signal and the tools to respond to it."
  },
  {
    icon: Shield,
    text: "Everything is private. Your journal, your mood, your data — visible only to you."
  },
  {
    icon: Gift,
    text: "Free forever. Upgrade only when you are ready for more."
  }
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  
  // Companion app trial params
  const [trialParams, setTrialParams] = useState(null);
  
  // Feedback form state
  const [feedbackName, setFeedbackName] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Parse companion app trial params from URL
  useEffect(() => {
    const trial = searchParams.get('trial');
    const trialDays = searchParams.get('trial_days');
    const trialTier = searchParams.get('trial_tier');
    
    if (trial || (trialDays && trialTier)) {
      // decodeURIComponent handles spaces/punctuation in anchor word
      const rawAnchor = searchParams.get('anchor');
      const params = {
        ref: searchParams.get('ref'),
        trial: trial,
        trial_days: parseInt(trialDays) || 14,
        trial_tier: trialTier || 'full',
        fallback_tier: searchParams.get('fallback_tier') || 'free',
        code: searchParams.get('code'),
        event: searchParams.get('event') ? decodeURIComponent(searchParams.get('event')) : null,
        anchor: rawAnchor ? decodeURIComponent(rawAnchor) : null
      };
      setTrialParams(params);
      // Store in sessionStorage for the registration flow
      sessionStorage.setItem('compass_trial_params', JSON.stringify(params));
    }
  }, [searchParams]);
  
  // Redirect logged-in users - activate trial if they have params
  useEffect(() => {
    const activateTrialForExistingUser = async () => {
      if (!loading && user && trialParams) {
        try {
          // Try to activate trial for existing user
          const response = await axios.post(`${API}/trial/activate-from-params`, trialParams, {
            withCredentials: true
          });
          
          // Handle different response scenarios
          if (response.data.trial_previously_used) {
            // User already used their one-time trial
            toast.info("You've already used your free trial. Upgrade to continue with full access.", {
              duration: 6000,
              action: {
                label: "Upgrade",
                onClick: () => window.open("https://www.truenorth-zone.com/pages/compass-membership", "_blank")
              }
            });
          } else if (response.data.already_active) {
            toast.success(`Your trial is still active with ${response.data.days_remaining} days remaining!`);
          } else if (response.data.success) {
            toast.success("Your full access trial is now active!");
          }
        } catch (err) {
          // Silently handle network errors - user may already have subscription
          if (process.env.NODE_ENV === 'development') {
            console.debug('Trial activation:', err.response?.data?.message || err.message);
          }
        }
        navigate("/compass", { replace: true });
      } else if (!loading && user) {
        navigate("/compass", { replace: true });
      }
    };
    
    activateTrialForExistingUser();
  }, [user, loading, navigate, trialParams]);
  
  // Handle feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setHasInteracted(true);
    
    if (!feedbackMessage.trim()) {
      setFeedbackError("Please enter a message");
      return;
    }
    
    setFeedbackLoading(true);
    setFeedbackError("");
    
    try {
      await axios.post(`${API}/feedback`, {
        name: feedbackName.trim() || null,
        email: feedbackEmail.trim() || null,
        message: feedbackMessage.trim(),
        marketing_consent: marketingConsent
      });
      
      setFeedbackSuccess(true);
      setFeedbackName("");
      setFeedbackEmail("");
      setFeedbackMessage("");
      setMarketingConsent(false);
    } catch (err) {
      // Only show error if user has interacted with the form
      if (hasInteracted) {
        setFeedbackError("Something went wrong. Please try again.");
      }
    } finally {
      setFeedbackLoading(false);
    }
  };
  
  // Don't render while checking auth - show nothing instead of error
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b2821] flex items-center justify-center">
        <Compass className="w-12 h-12 text-[#a69373] animate-pulse" />
      </div>
    );
  }
  
  // Don't render if user is logged in (will redirect)
  if (user) {
    return null;
  }
  
  return (
    <>
      <Helmet>
        <title>Compass — Free Daily Transformation Tools</title>
        <meta name="description" content="See your patterns. Change your response. Free mood tracking, journaling, breathing tools and an AI companion for reflection. Start in under 2 minutes." />
      </Helmet>
      
      <div className="min-h-screen bg-[#0b2821]">
        {/* ===== HERO SECTION ===== */}
        <section 
          className="relative min-h-[100svh] flex flex-col items-center justify-center px-4 py-8"
          style={{
            backgroundImage: 'url(/images/welcome-hero.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Dark overlay - 25% opacity so image shows at 75% */}
          <div className="absolute inset-0 bg-black/25" />
          
          {/* Content */}
          <div className="relative z-10 text-center max-w-lg mx-auto welcome-hero-text">
            {/* Logo */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <Compass className="w-8 h-8" style={{ color: '#ffffff' }} />
              <span className="text-2xl">Compass</span>
            </div>
            
            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl mb-4 leading-tight">
              See your patterns.<br />Change your response.
            </h1>
            
            {/* Subheading */}
            <p className="text-base sm:text-lg mb-8 leading-relaxed">
              A free daily practice that helps you understand your stress triggers, track your emotions, and respond to life with intention — not reaction.
            </p>
            
            {/* CTA Button */}
            <Button
              onClick={() => navigate("/register")}
              className="w-full sm:w-auto px-8 py-6 text-lg font-semibold rounded-xl transition-all hover:scale-105"
              style={{ 
                backgroundColor: '#a69373', 
                color: '#ffffff',
                boxShadow: '0 4px 20px rgba(166, 147, 115, 0.4)'
              }}
              data-testid="hero-cta"
            >
              Start Free — No Card Needed
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <div className="w-1.5 h-3 bg-white/50 rounded-full animate-bounce" />
            </div>
          </div>
        </section>
        
        {/* ===== FREE TOOLS SECTION ===== */}
        <section className="px-4 py-16 bg-[#0b2821]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-center text-2xl sm:text-3xl font-heading text-[#a69373] mb-10">
              Everything below is completely free.
            </h2>
            
            {/* Tools Grid - 2x3 mobile, 3x2 desktop */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {FREE_TOOLS.map((tool) => (
                <Card 
                  key={tool.name}
                  className="bg-[#0b2821] border border-[#a69373]/20 hover:border-[#a69373]/40 transition-colors"
                >
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 rounded-xl bg-[#a69373]/15 flex items-center justify-center mx-auto mb-3">
                      <tool.icon className="w-6 h-6 text-[#a69373]" />
                    </div>
                    <h3 className="font-medium text-white text-sm sm:text-base mb-1">
                      {tool.name}
                    </h3>
                    <p className="text-white/60 text-xs sm:text-sm leading-snug">
                      {tool.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* ===== SOCIAL PROOF SECTION ===== */}
        <section className="px-4 py-16 bg-[#0a2420]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-center text-2xl sm:text-3xl font-heading text-white mb-10">
              Your transformation starts with one check-in.
            </h2>
            
            <div className="space-y-4">
              {SOCIAL_PROOF.map((item) => (
                <Card 
                  key={item.text.substring(0, 20)}
                  className="bg-[#0b2821] border border-white/10"
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#a69373]/15 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-[#a69373]" />
                    </div>
                    <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                      {item.text}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* ===== PATHFINDER TEASER SECTION ===== */}
        <section className="px-4 py-16 bg-[#0b2821]">
          <div className="max-w-2xl mx-auto">
            <Card 
              className="overflow-hidden"
              style={{
                backgroundColor: 'rgba(30, 60, 50, 0.8)',
                borderLeft: '4px solid #a69373',
                borderTop: '1px solid rgba(166, 147, 115, 0.3)',
                borderRight: '1px solid rgba(166, 147, 115, 0.3)',
                borderBottom: '1px solid rgba(166, 147, 115, 0.3)'
              }}
            >
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-[#a69373]" />
                  <span className="text-[#a69373] text-sm font-medium uppercase tracking-wider">Premium Feature</span>
                </div>
                
                <h3 className="text-xl sm:text-2xl font-heading text-white mb-4">
                  Meet PathFinder — your AI companion.
                </h3>
                
                <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6">
                  PathFinder reads your journal entries, notices your patterns, and asks you the questions worth sitting with. Available on paid plans.
                </p>
                
                <Button
                  onClick={() => window.open('https://www.truenorth-zone.com/pages/compass-membership', '_blank')}
                  variant="outline"
                  className="border-[#a69373]/50 text-[#a69373] hover:bg-[#a69373]/10"
                  data-testid="pricing-btn"
                >
                  See Pricing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
        
        {/* ===== FEEDBACK SECTION ===== */}
        <section className="px-4 py-16 bg-[#0a2420]">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-xl bg-[#a69373]/15 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-6 h-6 text-[#a69373]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-[#a69373] mb-3">
                We want to hear from you.
              </h2>
              <p className="text-white/70 text-sm sm:text-base">
                Tell us what you think, what you need, or what would make Compass better for you.
              </p>
            </div>
            
            {feedbackSuccess ? (
              <Card className="bg-[#0b2821] border border-[#a69373]/30">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-[#a69373] mx-auto mb-4" />
                  <p className="text-white text-lg font-medium">
                    Thank you. Your feedback shapes what Compass becomes.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#0b2821] border border-[#a69373]/20">
                <CardContent className="p-6">
                  <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                    {/* Name Field */}
                    <div>
                      <label className="block text-white/60 text-sm mb-1.5">
                        Name <span className="text-white/40">(optional)</span>
                      </label>
                      <Input
                        type="text"
                        value={feedbackName}
                        onChange={(e) => setFeedbackName(e.target.value)}
                        placeholder="Your name"
                        className="bg-[#0a2420] border-[#a69373]/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    
                    {/* Email Field */}
                    <div>
                      <label className="block text-white/60 text-sm mb-1.5">
                        Email <span className="text-white/40">(optional)</span>
                      </label>
                      <Input
                        type="email"
                        value={feedbackEmail}
                        onChange={(e) => setFeedbackEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="bg-[#0a2420] border-[#a69373]/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    
                    {/* Message Field */}
                    <div>
                      <label className="block text-white/60 text-sm mb-1.5">
                        Message <span className="text-[#a69373]">*</span>
                      </label>
                      <Textarea
                        value={feedbackMessage}
                        onChange={(e) => setFeedbackMessage(e.target.value)}
                        placeholder="Share your thoughts, questions, or what brought you here..."
                        className="bg-[#0a2420] border-[#a69373]/20 text-white placeholder:text-white/40 min-h-[120px]"
                        required
                      />
                    </div>
                    
                    {/* Marketing Consent Checkbox */}
                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox
                        id="marketing-consent"
                        checked={marketingConsent}
                        onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                        className="mt-0.5 border-[#a69373]/40 data-[state=checked]:bg-[#a69373] data-[state=checked]:border-[#a69373]"
                      />
                      <label 
                        htmlFor="marketing-consent" 
                        className="text-white/60 text-sm cursor-pointer leading-relaxed"
                      >
                        I am happy for TrueNorth Zone to use my feedback in their marketing materials.
                      </label>
                    </div>
                    
                    {/* Error Message */}
                    {feedbackError && (
                      <p className="text-red-400 text-sm">{feedbackError}</p>
                    )}
                    
                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={feedbackLoading || !feedbackMessage.trim()}
                      className="w-full py-5 font-medium"
                      style={{ 
                        backgroundColor: '#a69373', 
                        color: '#ffffff',
                        opacity: feedbackLoading || !feedbackMessage.trim() ? 0.6 : 1
                      }}
                    >
                      {feedbackLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Feedback"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
        
        {/* ===== FINAL CTA SECTION ===== */}
        <section 
          className="px-4 py-20 sm:py-24"
          style={{
            background: 'linear-gradient(to bottom, #0b2821, #0a1f1a)'
          }}
        >
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-heading text-white mb-6">
              Ready to stop reacting and start navigating?
            </h2>
            
            <Button
              onClick={() => navigate("/register")}
              className="w-full sm:w-auto px-10 py-6 text-lg font-semibold rounded-xl transition-all hover:scale-105"
              style={{ 
                backgroundColor: '#a69373', 
                color: '#ffffff',
                boxShadow: '0 4px 20px rgba(166, 147, 115, 0.4)'
              }}
              data-testid="final-cta"
            >
              Create Your Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-white/50 text-sm mt-4">
              Takes less than 2 minutes. No card required. Cancel anytime.
            </p>
          </div>
        </section>
        
        {/* Footer spacer */}
        <div className="h-8 bg-[#0a1f1a]" />
      </div>
    </>
  );
}
